import {
  APPLICATIONS,
  ApplicationEnum,
  type ApplicationConfig,
} from "@repo/utils";
import { Checkbox } from "@repo/ui";
import type { ReactNode } from "react";

type Application = ApplicationConfig["cle"];

/** Effet de l'entretien structuré au-delà des campagnes : la gestion des scorecards. */
const NOTE_SCORECARDS =
  "FairView donne aussi accès à la gestion des scorecards.";

/**
 * Cases des applications (épreuves) qu'une organisation ou un groupe peut
 * proposer dans ses campagnes. `proposees` borne la liste : tout le catalogue
 * pour une organisation, les applications de l'organisation pour un groupe.
 * `reglages` : ce qui se règle pour une application cochée, affiché sous sa
 * case (ex. la version du COPSOQ III d'une organisation).
 */
export function ChoixApplications({
  idPrefix,
  proposees,
  value,
  onChange,
  reglages,
}: {
  idPrefix: string;
  proposees?: readonly Application[];
  value: readonly Application[];
  onChange: (applications: Application[]) => void;
  reglages?: Partial<Record<Application, ReactNode>>;
}) {
  const options = proposees
    ? APPLICATIONS.filter((a) => proposees.includes(a.cle))
    : APPLICATIONS;
  const toutesCochees = options.every((a) => value.includes(a.cle));

  // Toujours dans l'ordre du catalogue : c'est l'ordre d'affichage partout.
  const basculer = (cle: Application) =>
    onChange(
      options
        .map((a) => a.cle)
        .filter((c) => (c === cle ? !value.includes(c) : value.includes(c))),
    );

  const avecEntretien = options.some((a) => a.cle === ApplicationEnum.ENT_STRUCT);

  return (
    <div className="mt-3">
      <ul className="divide-y divide-charte-line overflow-hidden rounded-surface border border-charte-line">
        {options.map((a) => {
          const id = `${idPrefix}-${a.cle}`;
          const cochee = value.includes(a.cle);
          const reglage = cochee ? reglages?.[a.cle] : undefined;
          return (
            <li
              key={a.cle}
              className="px-4 py-2.5 transition-colors hover:bg-charte-hover"
            >
              <div className="flex items-center gap-3">
                {/* `htmlFor` plutôt qu'un <label> englobant : la Checkbox du
                    design system est un bouton, pas un input natif. */}
                <Checkbox
                  id={id}
                  checked={cochee}
                  onCheckedChange={() => basculer(a.cle)}
                />
                <label
                  htmlFor={id}
                  className="flex flex-1 cursor-pointer items-baseline justify-between gap-4"
                >
                  <span className="text-sm">{a.libelle}</span>
                  {a.choixALaCreation && (
                    <span className="text-right text-xs text-muted-foreground">
                      {a.choixALaCreation} au choix
                    </span>
                  )}
                </label>
              </div>
              {/* Aligné sur le libellé : retrait de la case (1rem) + l'écart. */}
              {reglage && <div className="mt-2 pl-7">{reglage}</div>}
            </li>
          );
        })}
      </ul>
      <div className="mt-2 flex items-start justify-between gap-4 text-xs">
        <p className="text-muted-foreground">
          {avecEntretien && NOTE_SCORECARDS}
        </p>
        <button
          type="button"
          className="shrink-0 text-primary hover:underline"
          onClick={() => onChange(toutesCochees ? [] : options.map((a) => a.cle))}
        >
          {toutesCochees ? "Tout décocher" : "Tout cocher"}
        </button>
      </div>
    </div>
  );
}
